'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["isReplaceInput"],
  _excluded2 = ["isOpen"],
  _excluded3 = ["isCloseButton"];
import { useState, useEffect, useRef } from 'react';
import I18n from 'I18n';
import styled from 'styled-components';
import { GYPSUM, CALYPSO, FLINT, SLINKY, BATTLESHIP, GREAT_WHITE } from 'HubStyleTokens/colors';
import UIFlex from 'UIComponents/layout/UIFlex';
import AccordionTransition from 'UIComponents/transitions/AccordionTransition';
import UIIconButton from 'UIComponents/button/UIIconButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITextInput from 'UIComponents/input/UITextInput';
import UIButton from 'UIComponents/button/UIButton';
import HR from 'UIComponents/elements/HR';
import { KeyTypes } from '../../constants/popover';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FindReplaceContainer = styled.div.withConfig({
  displayName: "FindReplace__FindReplaceContainer",
  componentId: "ruit6-0"
})(["background-color:", ";padding:4px;border:1px solid ", ";border-radius:3px;box-shadow:0 1px 24px 0 rgba(0,0,0,0.08);"], GYPSUM, BATTLESHIP);
const FindReplaceInput = styled(props => {
  const rest = _objectWithoutPropertiesLoose(props, _excluded);
  return /*#__PURE__*/_jsx(UITextInput, Object.assign({}, rest));
}).withConfig({
  displayName: "FindReplace__FindReplaceInput",
  componentId: "ruit6-1"
})(["&&&{border:none;font-size:13px;padding:0 4px;margin-left:", ";height:30px;width:140px;}&&&:focus{box-shadow:none;outline:none;}"], ({
  isReplaceInput
}) => isReplaceInput ? '18px' : '0');
const ToggleReplaceButton = styled(props => {
  const rest = _objectWithoutPropertiesLoose(props, _excluded2);
  return /*#__PURE__*/_jsx(UIIconButton, Object.assign({}, rest));
}).withConfig({
  displayName: "FindReplace__ToggleReplaceButton",
  componentId: "ruit6-2"
})(["margin:0 3px;transform:rotate(", ");transition-property:transform;transition-duration:300ms;transition-timing-function:'ease';"], ({
  isOpen
}) => isOpen ? '90deg' : '0');
const MatchCount = styled.div.withConfig({
  displayName: "FindReplace__MatchCount",
  componentId: "ruit6-3"
})(["color:", ";width:68px;text-align:left;font-size:13px;height:22px;margin:0 2px;"], FLINT);
const ActionButton = styled(props => {
  const rest = _objectWithoutPropertiesLoose(props, _excluded3);
  return /*#__PURE__*/_jsx(UIIconButton, Object.assign({}, rest));
}).withConfig({
  displayName: "FindReplace__ActionButton",
  componentId: "ruit6-4"
})(["font-size:", " !important;margin:0 !important;border-radius:13px !important;&&&:hover{background-color:", ";}"], ({
  isCloseButton
}) => isCloseButton ? '10px' : '14px', GREAT_WHITE);
const VerticalDivider = styled.div.withConfig({
  displayName: "FindReplace__VerticalDivider",
  componentId: "ruit6-5"
})(["border-left:1px solid ", ";height:26px;margin-left:6px;margin-right:6px;"], BATTLESHIP);
const HorizontalDivider = styled(HR).withConfig({
  displayName: "FindReplace__HorizontalDivider",
  componentId: "ruit6-6"
})(["margin:4px 0 4px 18px;width:calc(100% - 18px);background-color:", ";"], BATTLESHIP);
const ReplaceAllButton = styled(UIButton).withConfig({
  displayName: "FindReplace__ReplaceAllButton",
  componentId: "ruit6-7"
})(["min-width:144px !important;margin-left:2px;"]);
const i18nKey = key => `RichTextLib.findreplace.${key}`;
export default function FindReplace(props) {
  const {
    open,
    searchIndex,
    searchCount,
    findValue
  } = props;
  const [showReplace, setShowReplace] = useState(false);
  const [replaceValue, setReplaceValue] = useState('');
  const findInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  useEffect(() => {
    if (!open) {
      if (showReplace) setShowReplace(false);
      return;
    }
    // Focus on the proper text input when opened
    if (findInputRef.current && !showReplace) {
      findInputRef.current.focus();
    } else if (replaceInputRef.current && showReplace) {
      replaceInputRef.current.focus();
    }
  }, [open, showReplace]);
  const toggleShowReplace = () => {
    setShowReplace(!showReplace);
    if (props.onToggleShowReplace) props.onToggleShowReplace(!showReplace);
  };
  const handleFindInputChange = ({
    target: {
      value
    }
  }) => {
    props.onFindInputChange(value);
  };
  const handleFindNavigation = evt => {
    if (evt.key === KeyTypes.ENTER) {
      if (evt.shiftKey) {
        props.onFindPrev();
      } else {
        props.onFindNext();
      }
    }
  };
  const handleReplaceInputChange = ({
    target: {
      value
    }
  }) => {
    setReplaceValue(value);
  };
  const handleReplaceAllClick = () => {
    props.onReplaceAll(replaceValue);
  };
  const handleClose = ({
    key
  }) => {
    if (key === KeyTypes.ESCAPE) {
      props.onClose();
    }
  };
  const matchCount = `${searchCount < 10000 ? searchIndex : '- '}/${searchCount}`;
  const findPrevDisabled = searchIndex <= 1;
  const findNextDisabled = searchIndex >= searchCount;
  return /*#__PURE__*/_jsxs(FindReplaceContainer, {
    "data-test-id": "find-replace",
    onKeyDown: handleClose,
    children: [/*#__PURE__*/_jsxs(UIFlex, {
      align: "center",
      children: [/*#__PURE__*/_jsx(ToggleReplaceButton, {
        "data-test-id": "show-replace-toggle-button",
        use: "unstyled",
        size: "xs",
        onClick: toggleShowReplace,
        isOpen: showReplace,
        children: /*#__PURE__*/_jsx(UIIcon, {
          name: 'right',
          color: CALYPSO
        })
      }), /*#__PURE__*/_jsx(FindReplaceInput, {
        "data-test-id": "find-input",
        size: "small",
        type: "search",
        placeholder: I18n.text(i18nKey('findPlaceholder')),
        inputRef: findInputRef,
        value: findValue,
        onChange: handleFindInputChange,
        onKeyDown: handleFindNavigation
      }), /*#__PURE__*/_jsx(VerticalDivider, {}), /*#__PURE__*/_jsx(MatchCount, {
        "data-test-id": "find-match-count",
        children: matchCount
      }), /*#__PURE__*/_jsxs(UIFlex, {
        align: "center",
        justify: "end",
        children: [/*#__PURE__*/_jsx(ActionButton, {
          "data-test-id": "find-prev-button",
          use: "transparent",
          size: "xs",
          disabled: findPrevDisabled,
          onClick: props.onFindPrev,
          children: /*#__PURE__*/_jsx(UIIcon, {
            name: "up",
            color: findPrevDisabled ? FLINT : SLINKY
          })
        }, "find-prev"), /*#__PURE__*/_jsx(ActionButton, {
          "data-test-id": "find-next-button",
          use: "transparent",
          size: "xs",
          disabled: findNextDisabled,
          onClick: props.onFindNext,
          children: /*#__PURE__*/_jsx(UIIcon, {
            name: "down",
            color: findNextDisabled ? FLINT : SLINKY
          })
        }, "find-next"), /*#__PURE__*/_jsx(ActionButton, {
          "data-test-id": "find-replace-close-button",
          use: "transparent",
          size: "xs",
          isCloseButton: true,
          onClick: props.onClose,
          children: /*#__PURE__*/_jsx(UIIcon, {
            name: "remove",
            color: SLINKY
          })
        }, "close-find-replace")]
      })]
    }), /*#__PURE__*/_jsx(AccordionTransition, {
      open: showReplace,
      children: /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(HorizontalDivider, {}), /*#__PURE__*/_jsxs(UIFlex, {
          align: "center",
          children: [/*#__PURE__*/_jsx(FindReplaceInput, {
            "data-test-id": "replace-input",
            size: "small",
            type: "search",
            isReplaceInput: true,
            placeholder: I18n.text(i18nKey('replacePlaceholder')),
            inputRef: replaceInputRef,
            value: replaceValue,
            onChange: handleReplaceInputChange
          }), /*#__PURE__*/_jsx(VerticalDivider, {}), /*#__PURE__*/_jsx(ReplaceAllButton, {
            "data-test-id": "replace-all-button",
            use: "tertiary",
            size: "xs",
            disabled: searchCount === 0,
            onClick: handleReplaceAllClick,
            children: I18n.text(i18nKey('replaceAllText'))
          })]
        })]
      })
    })]
  });
}